import { ElementRef } from "@angular/core";
import { IGoogleMapsMarker } from "@interfaces/IGoogleMapsMarkerOptions";
import { IPlot } from "@interfaces/IPlot";
import { ColourService } from "@services/colour.service";
import { GoogleMarkerService, ICreateEventMarkerOptions } from "@services/googleMarker.service";
import { IPolyline, IPolylineOptions } from "@typings/google.maps.angular";
import * as angular from "angular";
import * as bigInt from "big-integer";
import { IVeriMapComponent } from "../directives/_map/veriMap/veriMapComponent/veriMap.component";
import { ConstantsService } from "./constantsService";

type BigInt = number;
declare const BigInt: typeof Number;

export class MapService {

    mapReadyPromises: angular.IPromise<google.maps.Map>[] = [];
    mapAPIReadyPromise: angular.IPromise<any>;
    mapAPIReady = false;

    // edge buffer for fitting markers within viewport
    boundsBuffer = 20; // was 50
    googleMapsApiKey: string;

    userLocation: GeolocationPosition;

    static $inject = ["$q", "$timeout", "constantsService", "colourService", "googleMarkerService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $timeout: angular.ITimeoutService,
        private readonly constantsService: ConstantsService,
        private readonly colourService: ColourService,
        private readonly googleMarkerService: GoogleMarkerService) {

        this.getUsersLocation();

        this.googleMapsApiKey = this.constantsService.getGoogleMapsApiKey();

        this.loadGoogleMapsScript({
            key: this.googleMapsApiKey,
            channel: "106315",
            libraries: "places,geometry,visualization,drawing",
            transport: "https",
            v: "quarterly",
            isGoogleMapsForWork: true
        });
    }

    getDefaultMapLocation(): google.maps.LatLng {
        return this.userLocation
            ? new google.maps.LatLng(this.userLocation.coords.latitude, this.userLocation.coords.longitude)
            : new google.maps.LatLng(-34.397, 150.644);
    }

    getGoogleMapsAPIUrl(isChina = false) {
        if (isChina) {
            return 'https://maps.google.cn/maps/api/js?';
        }

        return 'https://maps.googleapis.com/maps/api/js?';
    }

    loadGoogleMapsScript(options: any) {
        var query = "";
        var omitOptions = ['transport', 'isGoogleMapsForWork', 'china', 'preventLoad', 'key'];

        Object.keys(options).forEach(k => {
            if (omitOptions[k]) {
                return;
            }

            if (query) {
                query += "&";
            }

            query += `${k}=${options[k]}`;
        });

        query += "&callback=googleMapsCallback";

        var defer = this.$q.defer();
        this.mapAPIReadyPromise = defer.promise;

        window["googleMapsCallback"] = () => {

            this.loadMarkerWithLabelScript();
            this.loadMarkerClusterScript();

            this.waitForMapsToFullyLoad(defer);
        };

        var script = document.createElement("script");
        script.id = "google_maps_script";
        script.type = "text/javascript";
        script.async = true;
        script.src = this.getGoogleMapsAPIUrl(false) + query;

        document.head.appendChild(script);
    }

    private waitForMapsToFullyLoad(defer: angular.IDeferred<any>) {

        this.$timeout(() => {
            if (window["MarkerClusterer"]) {
                this.mapAPIReady = true;
                defer.resolve();
            }
            else {
                this.waitForMapsToFullyLoad(defer);
            }
        }, 10);
    }

    private loadMarkerWithLabelScript() {
        var script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = "/assets/js/markerwithlabel.js";
        script.async = true;
        document.head.appendChild(script);
    }

    private loadMarkerClusterScript() {
        var script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = "/assets/js/markerclusterer.js";
        script.async = true;
        document.head.appendChild(script);
    }

    findVeriMap(elementRef: ElementRef<HTMLElement>): IVeriMapComponent {
        var i = 0;
        var parent: Node = elementRef.nativeElement;

        do {
            parent = parent.parentNode;

            if (parent.nodeName.toLowerCase() == "veri-map") {
                const element = window.angular.element(parent);
                const scope: any = element.length && (element.isolateScope() || element.scope());

                return scope.$ctrl;
            }
        }
        while (parent && i++ < 100);
    }

    createMap(container: Element, myOptions?: google.maps.MapOptions): angular.IPromise<google.maps.Map> {
        var map = new google.maps.Map(container, myOptions);

        var defer = this.$q.defer<google.maps.Map>();
        var mapReadyPromise = defer.promise;
        this.mapReadyPromises.push(mapReadyPromise);

        google.maps.event.addListenerOnce(map, "idle", () => {
            defer.resolve(map);
        });

        return mapReadyPromise;
    }

    getMapAPIReadyPromise(): angular.IPromise<any> {
        return this.mapAPIReadyPromise;
    }

    convertLatLngToPoint(latLng: google.maps.LatLng, map: google.maps.Map, relativeToWindow = false): google.maps.Point {
        if (map == null) {
            console.error("Map object is null");
        }

        const topRight = map.getProjection().fromLatLngToPoint(map.getBounds().getNorthEast());
        const bottomLeft = map.getProjection().fromLatLngToPoint(map.getBounds().getSouthWest());
        const scale = Math.pow(2, map.getZoom());
        const worldPoint = map.getProjection().fromLatLngToPoint(latLng);

        var x = (worldPoint.x - bottomLeft.x) * scale;
        var y = (worldPoint.y - topRight.y) * scale;

        if (relativeToWindow) {
            var mapRect = map.getDiv().getBoundingClientRect();
            x += mapRect.left;
            y += mapRect.top;
        }

        return new google.maps.Point(x, y);
    }

    convertPointToLatLng(point, map: google.maps.Map): google.maps.LatLng {
        const topRight = map.getProjection().fromLatLngToPoint(map.getBounds().getNorthEast());
        const bottomLeft = map.getProjection().fromLatLngToPoint(map.getBounds().getSouthWest());
        const scale = Math.pow(2, map.getZoom());
        const worldPoint = new google.maps.Point(point.x / scale + bottomLeft.x, point.y / scale + topRight.y);

        return map.getProjection().fromPointToLatLng(worldPoint);
    }

    updateMapSize(map: google.maps.Map) {
        if (map) {
            google.maps.event.trigger(map, "resize");
        } else {
            console.error("No map provided to updateMapSize");
        }
    }

    getDistance(fromLat: number, fromLng: number, toLat: number, toLng: number, inMetres = false): number {

        // Taken from http://www.movable-type.co.uk/scripts/latlong.html

        const radius = 6378137; // earth radius in metres
        const a1 = this.toRadians(fromLat);
        const a2 = this.toRadians(toLat);
        const b1 = this.toRadians(toLat - fromLat);
        const b2 = this.toRadians(toLng - fromLng);

        const a = Math.sin(b1 / 2) * Math.sin(b1 / 2) +
            Math.cos(a1) *
            Math.cos(a2) *
            Math.sin(b2 / 2) *
            Math.sin(b2 / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        let distance = radius * c;

        if (inMetres) {
            return distance;
        }

        distance = parseFloat((distance / 1000).toFixed(0));

        return distance;
    }

    private toRadians(value: number): number {

        /** Converts numeric degrees to radians */
        return 0.017453 * value;
    }

    enablePOI(map: google.maps.Map) {
        const styles = [
            {
                featureType: "poi.business",
                stylers: [
                    { visibility: "on" }
                ]
            }
        ];

        map.setOptions({ styles: styles } as any);
    }

    setStyle(map: google.maps.Map, styles = null) {
        if (styles) {
            map.set('styles', styles);
            return;
        }

        var styles: any = [
            {
                "featureType": "administrative",
                "elementType": "all",
                "stylers": [{ "visibility": "on" }, { "lightness": 33 }]
            },
            {
                "featureType": "administrative",
                "elementType": "labels",
                "stylers": [{ "saturation": "-100" }]
            },
            {
                "featureType": "administrative",
                "elementType": "labels.text",
                "stylers": [
                    {
                        "gamma": "0.75"
                    }
                ]
            },
            {
                "featureType": "administrative.neighborhood",
                "elementType": "labels.text.fill",
                "stylers": [
                    {
                        "lightness": "-37"
                    }
                ]
            },
            {
                "featureType": "landscape",
                "elementType": "geometry",
                "stylers": [
                    {
                        "color": "#f9f9f9"
                    }
                ]
            },
            {
                "featureType": "landscape.man_made",
                "elementType": "geometry",
                "stylers": [
                    {
                        "saturation": "-100"
                    },
                    {
                        "lightness": "40"
                    },
                    {
                        "visibility": "off"
                    }
                ]
            },
            {
                "featureType": "landscape.natural",
                "elementType": "labels.text.fill",
                "stylers": [
                    {
                        "saturation": "-100"
                    },
                    {
                        "lightness": "-37"
                    }
                ]
            },
            {
                "featureType": "landscape.natural",
                "elementType": "labels.text.stroke",
                "stylers": [
                    {
                        "saturation": "-100"
                    },
                    {
                        "lightness": "100"
                    },
                    {
                        "weight": "2"
                    }
                ]
            },
            {
                "featureType": "landscape.natural",
                "elementType": "labels.icon",
                "stylers": [
                    {
                        "saturation": "-100"
                    }
                ]
            },
            {
                "featureType": "poi",
                "elementType": "geometry",
                "stylers": [
                    {
                        "saturation": "-100"
                    },
                    {
                        "lightness": "80"
                    }
                ]
            },
            {
                "featureType": "poi",
                "elementType": "labels",
                "stylers": [
                    {
                        "saturation": "-100"
                    },
                    {
                        "lightness": "0"
                    }
                ]
            },
            {
                "featureType": "poi.attraction",
                "elementType": "geometry",
                "stylers": [
                    {
                        "lightness": "-4"
                    },
                    {
                        "saturation": "-100"
                    }
                ]
            },
            {
                "featureType": "poi.park",
                "elementType": "geometry",
                "stylers": [
                    {
                        "color": "#c5dac6"
                    },
                    {
                        "visibility": "on"
                    },
                    {
                        "saturation": "-95"
                    },
                    {
                        "lightness": "62"
                    }
                ]
            },
            {
                "featureType": "poi.park",
                "elementType": "labels",
                "stylers": [
                    {
                        "visibility": "on"
                    },
                    {
                        "lightness": 20
                    }
                ]
            },
            {
                "featureType": "road",
                "elementType": "all",
                "stylers": [
                    {
                        "lightness": 20
                    }
                ]
            },
            {
                "featureType": "road.highway",
                "elementType": "geometry",
                "stylers": [
                    {
                        "color": "#c5c6c6"
                    },
                    {
                        "saturation": "-100"
                    }
                ]
            },
            {
                "featureType": "road.arterial",
                "elementType": "geometry",
                "stylers": [
                    {
                        "color": "#e4d7c6"
                    },
                    {
                        "saturation": "-100"
                    },
                    {
                        "lightness": "47"
                    }
                ]
            },
            {
                "featureType": "road.arterial",
                "elementType": "geometry.stroke",
                "stylers": [
                    {
                        "lightness": "-12"
                    }
                ]
            },
            {
                "featureType": "road.local",
                "elementType": "geometry",
                "stylers": [
                    {
                        "color": "#fbfaf7"
                    },
                    {
                        "lightness": "77"
                    }
                ]
            },
            {
                "featureType": "road.local",
                "elementType": "geometry.fill",
                "stylers": [
                    {
                        "saturation": "-100"
                    },
                    {
                        "lightness": "-5"
                    },
                    {
                        "gamma": "1"
                    }
                ]
            },
            {
                "featureType": "road.local",
                "elementType": "geometry.stroke",
                "stylers": [
                    {
                        "lightness": "-15"
                    },
                    {
                        "saturation": "-100"
                    }
                ]
            },
            {
                "featureType": "transit.station.airport",
                "elementType": "geometry",
                "stylers": [
                    {
                        "lightness": "47"
                    },
                    {
                        "saturation": "-100"
                    }
                ]
            },
            {
                "featureType": "water",
                "elementType": "all",
                "stylers": [
                    {
                        "visibility": "on"
                    },
                    {
                        "color": "#acbcc9"
                    }
                ]
            },
            {
                "featureType": "water",
                "elementType": "geometry",
                "stylers": [
                    {
                        "saturation": "53"
                    }
                ]
            },
            {
                "featureType": "water",
                "elementType": "labels.text.fill",
                "stylers": [{ "lightness": "-42" }, { "saturation": "17" }]
            },
            {
                "featureType": "water",
                "elementType": "labels.text.stroke",
                "stylers": [{ "lightness": "61" }]
            }
        ];

        var styles2 = [
            {
                "featureType": "landscape.man_made",
                "elementType": "geometry.fill",
                "stylers": [
                    {
                        "color":
                            "#ece2d9"
                    }
                ]
            }, {
                "featureType": "landscape.natural",
                "elementType": "geometry.fill",
                "stylers": [{ "visibility": "on" }, { "color": "#EBE5E0" }]
            }, { "featureType": "poi", "elementType": "all", "stylers": [{ "visibility": "on" }] },
            { "featureType": "poi.park", "elementType": "all", "stylers": [{ "visibility": "on" }] },
            { "featureType": "poi.park", "elementType": "geometry.fill", "stylers": [{ "color": "#ccdca1" }] },
            { "featureType": "poi.sports_complex", "elementType": "all", "stylers": [{ "visibility": "on" }] },
            {
                "featureType": "road",
                "elementType": "geometry.fill",
                "stylers": [{ "hue": "#ff0000" }, { "saturation": -100 }, { "lightness": 99 }]
            }, {
                "featureType": "road",
                "elementType": "geometry.stroke",
                "stylers": [{ "color": "#808080" }, { "lightness": 54 }]
            }, { "featureType": "road", "elementType": "labels.text.fill", "stylers": [{ "color": "#767676" }] },
            { "featureType": "road", "elementType": "labels.text.stroke", "stylers": [{ "color": "#ffffff" }] },
            {
                "featureType": "water",
                "elementType": "all",
                "stylers": [{ "saturation": 43 }, { "lightness": -11 }, { "hue": "#0088ff" }]
            }, {
                "featureType": "water",
                "elementType": "geometry.fill",
                "stylers": [{ "saturation": "-20" }, { "lightness": "40" }]
            }
        ];

        map.set("styles", styles2);
    }

    public panToWithOffset(map: google.maps.Map, latlng: google.maps.LatLng, offsetx: number, offsety: number) {
        var point1 = map.getProjection()
            .fromLatLngToPoint(
                (latlng instanceof google.maps.LatLng) ? latlng : map.getCenter()
            );

        var scale = Math.pow(2, map.getZoom());

        var point2 = new google.maps.Point((offsetx / scale) || 0, (offsety / scale) || 0);

        map.panTo(map.getProjection()
            .fromPointToLatLng(new google.maps.Point(
                point1.x + point2.x,
                point1.y + point2.y
            )));
    }

    public zoomOnPoint(map: google.maps.Map, latlng: google.maps.LatLng) {
        var circle = new google.maps.Circle();

        circle.setCenter(latlng);
        circle.setRadius(1000);

        var bounds = circle.getBounds();

        map.fitBounds(bounds);
    }

    smoothZoom(map: google.maps.Map, target: number, currentZoom?: number, callback?: Function) {
        currentZoom = currentZoom || map.getZoom();

        // If we check for equality, we will always stop 1 zoom too short, hence the need for +1
        if (currentZoom > target) {
            if (callback) {
                callback();
            }

            return;
        }

        google.maps.event.addListenerOnce(map, "zoom_changed", (event) => {
            return this.smoothZoom(map, target, target < currentZoom ? currentZoom - 1 : currentZoom + 1, callback);
        });

        setTimeout(() => {
            map.setZoom(currentZoom)
        }, 150); // 80ms
    }

    zoomAndCallback(map: google.maps.Map, zoomLevel: number, callback: Function, timeout?: number) {
        google.maps.event.addListenerOnce(map, "zoom_changed", () => {
            callback();
        });

        setTimeout(() => { map.setZoom(zoomLevel) }, timeout || 80); // 80ms
    }

    smoothPanAndZoomTo(map: google.maps.Map, latLng: google.maps.LatLng, targetZoom: number): angular.IPromise<void> {
        var defer = this.$q.defer<void>();

        const actionInterval = 300;//150

        // Check if position is within the current maps bounds, if so pan
        if (map.getCenter().equals(latLng)) {

            // If we are at the correct position then we just need to zoom to the correct level
            if (map.getZoom() !== targetZoom) {

                setTimeout(() => {
                    this.smoothZoom(map, targetZoom, null, () => { defer.resolve(); });

                }, actionInterval);
            } else {
                // done
                defer.resolve();
            }
        }
        else if (map.getBounds().contains(latLng)) {
            setTimeout(() => {
                map.panTo(latLng);

                setTimeout(() => {
                    this.smoothPanAndZoomTo(map, latLng, targetZoom).then(() => {
                        defer.resolve();
                    });
                }, actionInterval);
            }, actionInterval);
        }
        else {
            // Zoom out until we can pan
            this.zoomAndCallback(map, map.getZoom() - 1, () => {
                this.smoothPanAndZoomTo(map, latLng, targetZoom).then(() => {
                    defer.resolve();
                });
            });
        }
        return defer.promise;
    }

    applyOffsetToLatLng(gmap: google.maps.Map, latLng: google.maps.LatLng, mapOffset: google.maps.Point): google.maps.LatLng {
        var originalPoint = this.convertLatLngToPoint(latLng, gmap);

        originalPoint.x += mapOffset.x;
        originalPoint.y += mapOffset.y;

        var adjustedLatLng = this.convertPointToLatLng(originalPoint, gmap);
        return adjustedLatLng;
    }

    getBoundsWithOffset(gmap: google.maps.Map, mapOffset: google.maps.Point): google.maps.LatLngBounds {
        if (gmap == null) {
            console.error("mapService.getBoundsWithOffset - gmap = null");
            return;
        }

        if (mapOffset == null) {
            mapOffset = new google.maps.Point(0, 0);
        }

        const bounds = gmap.getBounds();
        const newBounds = new google.maps.LatLngBounds();

        var topRightPoint = this.convertLatLngToPoint(bounds.getNorthEast(), gmap);
        topRightPoint.x += mapOffset.x - this.boundsBuffer;
        topRightPoint.y += this.boundsBuffer;
        var offsetTopRight = this.convertPointToLatLng(topRightPoint, gmap);

        var bottomLeftPoint = this.convertLatLngToPoint(bounds.getSouthWest(), gmap);
        bottomLeftPoint.x += this.boundsBuffer;
        bottomLeftPoint.y -= this.boundsBuffer;
        var offsetBottomLeft = this.convertPointToLatLng(bottomLeftPoint, gmap);

        newBounds.extend(offsetTopRight);
        newBounds.extend(offsetBottomLeft);

        return newBounds;
    }

    setMapBoundsWithOffset(gmap: google.maps.Map, latLngs: google.maps.LatLng[] /*| google.maps.LatLngLiteral[]*/, mapOffset: google.maps.Point | { x: number, y: number } = new google.maps.Point(0, 0)): void {

        const bounds = this.getBoundsFromLatLngArray(latLngs);
        const mapDiv = gmap.getDiv();

        const dimensions = {
            width: mapDiv.clientWidth - Math.abs(mapOffset.x) - (this.boundsBuffer * 2),
            height: mapDiv.clientHeight - Math.abs(mapOffset.y) - (this.boundsBuffer * 2)
        };

        // For single items we will zoom out a bit to show context
        const maxZoom = latLngs.length == 1 ? 18 : 25;

        const zoomLevel = Math.min(this.getBoundsZoomLevel(bounds, dimensions), maxZoom);

        if (zoomLevel !== gmap.getZoom()) {
            gmap.setZoom(zoomLevel);
        }

        this.setOffsetCenter(gmap, bounds.getCenter(), mapOffset);
    }

    setOffsetCenter(gmap: google.maps.Map, latlng: google.maps.LatLng, mapOffset: google.maps.Point | { x: number, y: number }) {

        if (!mapOffset) {
            mapOffset = new google.maps.Point(0, 0);
        }
        else {
            //console.log("offset", mapOffset);
        }

        const newCenterLatLng = this.offsetLatLng(gmap, latlng, mapOffset.x / 2, mapOffset.y / 2);
        const newCenterLat = newCenterLatLng.lat();
        const newCenterLng = newCenterLatLng.lng();

        if (!isNaN(newCenterLat) && !isNaN(newCenterLng)) {
            gmap.panTo(newCenterLatLng);
        }
    };

    setCenter(gmap: google.maps.Map, latlng: google.maps.LatLng, mapOffset: google.maps.Point | { x: number, y: number }) {
        const offsetX = mapOffset ? mapOffset.x / 2 : 0;
        const offsetY = mapOffset ? mapOffset.y / 2 : 0;
        const newCenterLatLng = this.offsetLatLng(gmap, latlng, offsetX, offsetY);

        gmap.setCenter(newCenterLatLng);
    };

    offsetLatLng(gmap: google.maps.Map, latlng: google.maps.LatLng, offsetX: number, offsetY: number) {
        offsetX = offsetX || 0;
        offsetY = offsetY || 0;

        const scale = Math.pow(2, gmap.getZoom());
        const point = gmap.getProjection().fromLatLngToPoint(latlng);
        const pixelOffset = new google.maps.Point((offsetX / scale), (offsetY / scale));

        const newPoint = new google.maps.Point(
            point.x - pixelOffset.x,
            point.y + pixelOffset.y
        );

        return gmap.getProjection().fromPointToLatLng(newPoint);
    };

    getBoundsFromLatLngArray(locations: google.maps.LatLng[]): google.maps.LatLngBounds {
        const bounds = new google.maps.LatLngBounds();

        for (var i = 0; i < locations.length; i++) {
            if (!locations[i]?.lat || !locations[i]?.lng) {
                continue;
            }

            bounds.extend(locations[i]);
        }

        return bounds;
    }

    getBoundsZoomLevel(bounds: google.maps.LatLngBounds, dimensions: { width: number, height: number }) {

        dimensions.width = Math.max(1, dimensions.width);
        dimensions.height = Math.max(1, dimensions.height);

        const TILE_SIZE = { height: 256, width: 256 }; // google World tile size, as of v3.22
        const ZOOM_MAX = 21; // max google maps zoom level, as of v3.22

        const latRadian = lat => {
            let sin = Math.sin(lat * Math.PI / 180);
            let radX2 = Math.log((1 + sin) / (1 - sin)) / 2;

            return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
        };

        const zoom = (mapPx, worldPx, fraction) => {
            return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
        };

        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();

        const latFraction = (latRadian(ne.lat()) - latRadian(sw.lat())) / Math.PI;
        const lngDiff = ne.lng() - sw.lng();
        const lngFraction = ((lngDiff < 0) ? (lngDiff + 360) : lngDiff) / 360;

        const latZoom = zoom(dimensions.height, TILE_SIZE.height, latFraction);
        const lngZoom = zoom(dimensions.width, TILE_SIZE.width, lngFraction);

        return Math.min(latZoom, lngZoom, ZOOM_MAX);
    }

    createMapMarker(plot: IPlot, options: ICreateEventMarkerOptions): IGoogleMapsMarker {
        options.position = {
            lat: plot.Latitude,
            lng: plot.Longitude
        };

        return {
            id: 1,
            options: this.googleMarkerService.createEventMarkerOptions(options)
        };
    }

    getDefaultPolylineOptions(color?: string, events?: any): IPolylineOptions {
        return {
            stroke: {
                color: color || "#00426a",
                weight: 3,
                opacity: 0.9
            },
            events: events
        }
    }

    createPolyline(id: number, latLngArray: google.maps.LatLng[], color?: string, events?: any, dashed?: boolean): IPolyline {
        const polylineOptions = (dashed)
            ? this.getDefaultDashedPolylineOptions(color, events)
            : this.getDefaultPolylineOptions(color, events);

        const polyline: IPolyline = {
            id: id,
            path: latLngArray,
            options: polylineOptions,
        };

        return polyline;
    }

    createPolylineFromPlots(plots: IPlot[], color = this.colourService.redColor) {
        const latLngArray = this.convertPlotsToGoogleLatLng(plots);
        const newPolyline = this.createPolyline(0, latLngArray, color, null, false);
        this.addDirectionArrows(newPolyline, color);

        return newPolyline;
    }

    addDirectionArrows(polyline: IPolyline, lineColor: string) {
        polyline.options.stroke.weight = 3;
        polyline.options.icons = [{
            icon: {
                path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                fillOpacity: 1,
                scale: 3,
                fillColor: "white",
                strokeColor: lineColor,
                strokeWeight: 1,
            },
            offset: "100%",
            repeat: "100px"
        }];
    }

    getDefaultDashedPolylineOptions(color?: string, events?: any): IPolylineOptions {
        var options = this.getDefaultPolylineOptions(color, events);
        options.stroke.opacity = 0;

        options.icons = [
            {
                icon: {
                    path: "M 0,-1 0,1",
                    strokeOpacity: 1,
                    scale: 2,
                    strokeWeight: 2
                },
                offset: "0",
                repeat: "10px"
            }
        ];

        return options;
    }

    catmullRomSpline(linePoints: number[][]): number[][] {
        const points: number[][] = [];

        var alpha = 0.5;
        // If 'alpha' is 0.5 then the 'Centripetal' variant is used
        // If 'alpha' is 1 then the 'Chordal' variant is used

        var p0, p1, p2, p3, bp1, bp2, d1, d2, d3, A, B, N, M;
        var d3powA, d2powA, d3pow2A, d2pow2A, d1pow2A, d1powA;
        //var linePoints;
        //var d = Math.round(linePoints[0][0]) + ',' + Math.round(linePoints[0][1]) + ' ';

        points.push([linePoints[0][0], linePoints[0][1]]);

        var length = linePoints.length;
        for (var i = 0; i < length - 1; i++) {

            p0 = i == 0 ? linePoints[0] : linePoints[i - 1];
            p1 = linePoints[i];
            p2 = linePoints[i + 1];
            p3 = i + 2 < length ? linePoints[i + 2] : p2;

            d1 = Math.sqrt(Math.pow(p0[0] - p1[0], 2) + Math.pow(p0[1] - p1[1], 2));
            d2 = Math.sqrt(Math.pow(p1[0] - p2[0], 2) + Math.pow(p1[1] - p2[1], 2));
            d3 = Math.sqrt(Math.pow(p2[0] - p3[0], 2) + Math.pow(p2[1] - p3[1], 2));

            // Catmull-Rom to Cubic Bezier conversion matrix

            // A = 2d1^2a + 3d1^a * d2^a + d3^2a
            // B = 2d3^2a + 3d3^a * d2^a + d2^2a

            // [   0             1            0          0          ]
            // [   -d2^2a /N     A/N          d1^2a /N   0          ]
            // [   0             d3^2a /M     B/M        -d2^2a /M  ]
            // [   0             0            1          0          ]

            d3powA = Math.pow(d3, alpha);
            d3pow2A = Math.pow(d3, 2 * alpha);
            d2powA = Math.pow(d2, alpha);
            d2pow2A = Math.pow(d2, 2 * alpha);
            d1powA = Math.pow(d1, alpha);
            d1pow2A = Math.pow(d1, 2 * alpha);

            A = 2 * d1pow2A + 3 * d1powA * d2powA + d2pow2A;
            B = 2 * d3pow2A + 3 * d3powA * d2powA + d2pow2A;
            N = 3 * d1powA * (d1powA + d2powA);
            if (N > 0) {
                N = 1 / N;
            }

            M = 3 * d3powA * (d3powA + d2powA);
            if (M > 0) {
                M = 1 / M;
            }

            bp1 = [
                (-d2pow2A * p0[0] + A * p1[0] + d1pow2A * p2[0]) * N,
                (-d2pow2A * p0[1] + A * p1[1] + d1pow2A * p2[1]) * N
            ];

            bp2 = [
                (d3pow2A * p1[0] + B * p2[0] - d2pow2A * p3[0]) * M,
                (d3pow2A * p1[1] + B * p2[1] - d2pow2A * p3[1]) * M
            ];

            if (bp1[0] == 0 && bp1[1] == 0) {
                bp1 = p1;
            }

            if (bp2[0] == 0 && bp2[1] == 0) {
                bp2 = p2;
            }

            //d += 'C' + bp1[0] + ',' + bp1[1] + ' ' + bp2[0] + ',' + bp2[1] + ' ' + p2[0] + ',' + p2[1] + ' ';


            points.push([bp1[0], bp1[1]]);
            points.push([bp2[0], bp2[1]]);
            points.push([p2[0], p2[1]]);
        }

        return points;
    }

    bSplineLinePoints(linePoints: number[][]): number[][] {
        let i, t, ax, ay, bx, by, cx, cy, dx, dy, lat, lng;
        const points: number[][] = [];

        for (i = 2; i < linePoints.length - 2; i++) {
            for (t = 0; t < 1; t += 0.2) {
                ax = (-linePoints[i - 2][0] + 3 * linePoints[i - 1][0] - 3 * linePoints[i][0] + linePoints[i + 1][0]) / 6;
                ay = (-linePoints[i - 2][1] + 3 * linePoints[i - 1][1] - 3 * linePoints[i][1] + linePoints[i + 1][1]) / 6;

                bx = (linePoints[i - 2][0] - 2 * linePoints[i - 1][0] + linePoints[i][0]) / 2;
                by = (linePoints[i - 2][1] - 2 * linePoints[i - 1][1] + linePoints[i][1]) / 2;

                cx = (-linePoints[i - 2][0] + linePoints[i][0]) / 2;
                cy = (-linePoints[i - 2][1] + linePoints[i][1]) / 2;

                dx = (linePoints[i - 2][0] + 4 * linePoints[i - 1][0] + linePoints[i][0]) / 6;
                dy = (linePoints[i - 2][1] + 4 * linePoints[i - 1][1] + linePoints[i][1]) / 6;

                lat = (ax * Math.pow(t + 0.1, 3)) + (bx * Math.pow(t + 0.1, 2)) + (cx * (t + 0.1)) + dx;
                lng = (ay * Math.pow(t + 0.1, 3)) + (by * Math.pow(t + 0.1, 2)) + (cy * (t + 0.1)) + dy;

                points.push([lat, lng]);
            }
        }

        return points;
    }

    // Credit: mourner.github.io/simplify-js
    simplifyRoute(points: number[][], tolerance: number, highestQuality: boolean): number[][] {
        performance.mark("simplifyRoute");

        if (points.length <= 2) {
            return points;
        }

        const sqTolerance = tolerance !== undefined ? tolerance * tolerance : 1;

        points = highestQuality ? points : this.simplifyRadialDist(points, sqTolerance);
        points = this.simplifyDouglasPeucker(points, sqTolerance);

        performance.measure("simplifyRoute");

        return points;
    }

    // simplification using Ramer-Douglas-Peucker algorithm
    private simplifyDouglasPeucker(points: number[][], sqTolerance: number) {
        const last = points.length - 1;

        const simplified = [points[0]];
        this.simplifyDPStep(points, 0, last, sqTolerance, simplified);
        simplified.push(points[last]);

        return simplified;
    }

    private simplifyDPStep(points: number[][], first: number, last: number, sqTolerance: number, simplified: number[][]) {
        let maxSqDist = 0,
            index = 0;

        for (let i = first + 1; i < last; i++) {
            const sqDist = this.getSqSegDist(points[i], points[first], points[last]);

            if (sqDist > maxSqDist) {
                index = i;
                maxSqDist = sqDist;
            }
        }

        if (maxSqDist > sqTolerance) {
            if (index - first > 1) {
                this.simplifyDPStep(points, first, index, sqTolerance, simplified);
            }

            simplified.push(points[index]);

            if (last - index > 1) {
                this.simplifyDPStep(points, index, last, sqTolerance, simplified);
            }
        }
    }

    private getSqDist(p1: number[], p2: number[]) {

        const dx = p1[0] - p2[0];
        const dy = p1[1] - p2[1];

        return dx * dx + dy * dy;
    }

    // square distance from a point to a segment
    private getSqSegDist(p: number[], p1: number[], p2: number[]) {
        let x = p1[0];
        let y = p1[1];
        let dx = p2[0] - x;
        let dy = p2[1] - y;

        if (dx !== 0 || dy !== 0) {
            const t = ((p[0] - x) * dx + (p[1] - y) * dy) / (dx * dx + dy * dy);

            if (t > 1) {
                x = p2[0];
                y = p2[1];

            }
            else if (t > 0) {
                x += dx * t;
                y += dy * t;
            }
        }

        dx = p[0] - x;
        dy = p[1] - y;

        return dx * dx + dy * dy;
    }

    simplifyLatlngs(latLngs: google.maps.LatLng[], tolerance: number, highestQuality: boolean): google.maps.LatLng[] {
        var points: number[][] = [];

        for (var i = 0; i < latLngs.length; i++) {
            const latLng = latLngs[i];
            points.push([latLng.lat(), latLng.lng()]);
        }

        points = this.simplifyRoute(points, tolerance, highestQuality);

        latLngs = [];

        for (var i = 0; i < points.length; i++) {
            const point = points[i];
            latLngs.push(new google.maps.LatLng(point[0], point[1]));
        }

        return latLngs;
    }

    // BigInt version
    getPointsFromEncodedString(encodedLine: string): number[][] {

        // Use native where possible
        if (window["BigInt"]) {
            return this.getPointsFromEncodedStringNative(encodedLine);
        }

        performance.mark("getPointsFromEncodedString");
        const EncodingString = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-";

        var points: number[][] = [];

        if (!encodedLine) {
            return points;
        }

        var index = 0;
        var xsum = 0;
        var ysum = 0;

        while (index < encodedLine.length) {
            var n = bigInt.zero;
            var k = 0;

            while (true) {
                if (index >= encodedLine.length) {
                    return points;
                }

                var b = EncodingString.indexOf(encodedLine[index++]);

                if (b == -1) {
                    return points;
                }

                n = n.or(bigInt(b & 31).shiftLeft(k));

                k += 5;
                if (b < 32) {
                    break;
                }
            }

            var diagonal = Math.floor((Math.sqrt(8 * n.toJSNumber() + 5) - 1) / 2);

            n = n.subtract(diagonal * (diagonal + 1) / 2);

            var ny = n.toJSNumber();
            var nx = diagonal - ny;

            nx = (nx >> 1) ^ -(nx & 1);
            ny = (ny >> 1) ^ -(ny & 1);

            xsum += nx;
            ysum += ny;

            points.push([ysum * 0.00001, xsum * 0.00001]);
        }

        performance.measure("getPointsFromEncodedString");

        var countNow = points.length;
        points = this.simplifyRoute(points, 0.00001, true);
        var reduction = countNow - points.length;

        return points;
    }

    // basic distance-based simplification
    private simplifyRadialDist(points: number[][], sqTolerance: number) {

        let prevPoint = points[0];
        let point = points[0];

        const newPoints = [prevPoint];

        for (var i = 1, len = points.length; i < len; i++) {
            point = points[i];

            if (this.getSqDist(point, prevPoint) > sqTolerance) {
                newPoints.push(point);
                prevPoint = point;
            }
        }

        if (prevPoint !== point) {
            newPoints.push(point);
        }

        return newPoints;
    }

    getPointsFromEncodedStringNative(encodedLine) {

        var points = [];

        if (!encodedLine) {
            return points;
        }

        const EncodingString = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-";

        var index = 0;
        var xsum = 0;
        var ysum = 0;

        const bigInt0 = BigInt(0);
        const bigInt1 = BigInt(1);
        const bigInt2 = BigInt(2);
        const bigInt5 = BigInt(5);
        const bigInt31 = BigInt(31);
        const bigInt32 = BigInt(32);

        while (index < encodedLine.length) {
            var n = bigInt0;
            var k = bigInt0;

            while (true) {
                if (index >= encodedLine.length) {
                    return points;
                }

                var b = BigInt(EncodingString.indexOf(encodedLine[index++]));

                if (b === -1) {
                    return points;
                }

                n |= (b & bigInt31) << k;
                k += bigInt5;

                if (b < bigInt32) {
                    break;
                }
            }

            var diagonal = BigInt(Math.floor(((Math.sqrt(8 * Number(n) + 5) - 1) / 2)));

            n -= BigInt(diagonal * (diagonal + bigInt1) / bigInt2);

            var ny = n;
            var nx = diagonal - ny;

            nx = (nx >> bigInt1) ^ -(nx & bigInt1);
            ny = (ny >> bigInt1) ^ -(ny & bigInt1);

            xsum += Number(nx);
            ysum += Number(ny);

            points.push([ysum * 0.00001, xsum * 0.00001]);
        }

        return points;
    }

    // Todo: Move to map service?
    convertArrayToGoogleLatLng(plots: number[][]): google.maps.LatLng[] {
        if (!plots) {
            return [];
        }

        const latLngList: google.maps.LatLng[] = [];

        for (let i = 0; i < plots.length; i++) {
            const plot = plots[i];

            const latLng = new google.maps.LatLng(plot[0], plot[1]);
            latLngList.push(latLng);
        }

        return latLngList;
    }

    // Todo: Move to map service?
    convertPlotsToGoogleLatLng(plots: IPlot[]): google.maps.LatLng[] {
        if (!plots) {
            return [];
        }

        const latLngList: google.maps.LatLng[] = [];

        for (let i = 0; i < plots.length; i++) {
            const plot = plots[i];

            const latLng = new google.maps.LatLng(plot.Latitude, plot.Longitude);
            latLngList.push(latLng);
        }

        return latLngList;
    }

    private getUsersLocation() {
        navigator.geolocation.getCurrentPosition(
            location => {
                this.userLocation = location;
            });
    }
}
